import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { formatApiErrorDetail } from "../lib/api";
import { Lock, Eye, EyeOff } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email, password);
      navigate(u.role === "admin" ? "/admin" : "/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <div data-testid="login-card" className="w-full max-w-md rounded-2xl bg-white px-8 py-10 sm:px-12 sm:py-12 shadow-[0_10px_40px_rgba(15,23,42,0.08)] border border-slate-100 animate-fade-up">
        <div className="text-center mb-8">
          <h1 className="font-display text-3xl sm:text-4xl font-800 tracking-tight text-slate-900">
            YDeal <span className="text-[#2456B8]">&amp;</span> Title Priority
          </h1>
          <p className="mt-2 text-sm text-slate-400">Title Order Management System</p>
        </div>

        <form onSubmit={submit} className="space-y-5">
          <div>
            <label className="text-sm font-semibold text-slate-700 mb-1.5 block">Email Address</label>
            <input
              data-testid="login-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              style={{ backgroundColor: "#ffffff", color: "#0f172a" }}
              className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-sm text-slate-800 placeholder:text-slate-400 focus:border-[#2456B8] focus:ring-2 focus:ring-[#2456B8]/30 focus:outline-none transition-colors"
              placeholder="Enter your email"
            />
          </div>
          <div>
            <label className="text-sm font-semibold text-slate-700 mb-1.5 block">Password</label>
            <div className="relative">
              <input
                data-testid="login-password"
                type={showPw ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                style={{ backgroundColor: "#ffffff", color: "#0f172a" }}
                className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 pr-11 text-sm text-slate-800 placeholder:text-slate-400 focus:border-[#2456B8] focus:ring-2 focus:ring-[#2456B8]/30 focus:outline-none transition-colors"
                placeholder="Enter your password"
              />
              <button
                type="button"
                data-testid="login-toggle-password"
                onClick={() => setShowPw((v) => !v)}
                aria-label={showPw ? "Hide password" : "Show password"}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
              >
                {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          {error && (
            <div data-testid="login-error" className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              <Lock className="h-4 w-4 shrink-0" /> {error}
            </div>
          )}

          <button
            data-testid="login-submit"
            type="submit"
            disabled={loading}
            className="w-full rounded-lg bg-[#2456B8] px-4 py-3 text-sm font-bold text-white hover:bg-[#1d478f] transition-colors duration-200 disabled:opacity-60"
          >
            {loading ? "Signing in…" : "Sign In"}
          </button>
        </form>

        <p className="mt-6 text-center text-sm text-slate-400">
          Contact your administrator if you forgot your password.
        </p>
      </div>
    </div>
  );
}
